/**
 * Shared plumbing for the Arcware pixel-streaming session ("the Navigator").
 *
 * Used by the authenticated Navigator tab (ScenesProvider + PStreaming.tsx) and
 * by the public shared-scene page (SharedScenePStreaming.tsx). Everything here
 * reaches into the SDK through paths that exist in
 * @arcware-cloud/pixelstreaming-websdk 1.3.x:
 *
 * - the signalling socket lives at `pixelStreaming.webRtcController.transport.webSocket`
 * - UI messages to the Unreal instance go out through
 *   `pixelStreaming.webRtcController.dataChannelSender`, which silently drops a
 *   message when the data channel is not open yet
 * - `ArcwareInit` returns a module-level singleton on every call after the
 *   first (same share id), whose constructor — and with it AutoConnect — does
 *   not run again
 */

export const ARCWARE_SHARE_ID_STAGE =
	"share-25ffa203-3ea3-4463-93b8-590d4d751c26";
export const ARCWARE_SHARE_ID_PROD =
	"share-0c794eb2-7a9f-4df8-b5f6-8196e73f5ca1";

export const getArcwareShareId = (isStage: boolean) =>
	isStage ? ARCWARE_SHARE_ID_STAGE : ARCWARE_SHARE_ID_PROD;

/** SDK configuration shared by both Navigator entry points. */
export const buildArcwareConfig = () => ({
	initialSettings: {
		StartVideoMuted: true,
		AutoConnect: true,
		AutoPlayVideo: true,
		PreferredCodec: "H264",
		// The Unreal app looks around on a mouse drag and does nothing with raw
		// touch events, so a finger on a touchscreen could not turn the camera.
		// This makes the SDK send a single finger as that mouse drag instead.
		FakeMouseWithTouches: true,
	},
	settings: {
		infoButton: false,
		micButton: false,
		audioButton: false,
		fullscreenButton: false,
		settingsButton: false,
		connectionStrengthIcon: false,
		loveLetterLogging: false,
		connectionIdentifierLoggingDisabled: true,
	},
});

/**
 * Streaming states in which a session is connected to an Unreal instance and
 * can take a scene-load command. Outside of these the socket is closed or still
 * negotiating, and a load command would be dropped on the floor.
 */
export const LIVE_STREAMING_STATES = [
	"videoInitialized",
	"loadingScene",
	"sceneLoaded",
];

export const getWebSocketReadyState = (
	pixelStreaming: any,
): number | undefined =>
	pixelStreaming?.webRtcController?.transport?.webSocket?.readyState;

/**
 * True when `ArcwareInit` just constructed this instance and its AutoConnect
 * is in flight: the socket is CONNECTING and no video has been initialised yet.
 * Anything else means we were handed back the SDK singleton from an earlier
 * session, which will never connect (or fire videoInitialized again) on its
 * own — the caller has to `reconnect()` it.
 */
export const isFreshlyConnecting = (pixelStreaming: any): boolean =>
	getWebSocketReadyState(pixelStreaming) === WebSocket.CONNECTING &&
	pixelStreaming?.videoInitializedSent !== true;

export const canSendToStreamer = (pixelStreaming: any): boolean => {
	try {
		return (
			pixelStreaming?.webRtcController?.dataChannelSender?.canSend?.() ===
			true
		);
	} catch (e) {
		return false;
	}
};

/**
 * Runs `send` as soon as the data channel to the Unreal instance is open.
 *
 * `videoInitialized` fires on the first decoded frame, which can land a beat
 * before the data channel finishes opening; a scene-load command sent in that
 * gap is dropped by the SDK with nothing but a console error, and the Navigator
 * then waits forever for a scene nobody asked for. After `timeoutMs` the
 * message is sent anyway so the SDK's own error surfaces in the console.
 *
 * Returns a cancel function for callers that issue a newer command.
 */
export const whenStreamerReady = (
	pixelStreaming: any,
	send: () => void,
	{ intervalMs = 100, timeoutMs = 20000 } = {},
): (() => void) => {
	if (canSendToStreamer(pixelStreaming)) {
		send();
		return () => {};
	}
	const startedAt = Date.now();
	streamingLog("data channel not open yet, holding the scene-load command");
	const timer = window.setInterval(() => {
		if (canSendToStreamer(pixelStreaming)) {
			window.clearInterval(timer);
			streamingLog(`data channel open after ${Date.now() - startedAt}ms`);
			send();
			return;
		}
		if (Date.now() - startedAt >= timeoutMs) {
			window.clearInterval(timer);
			streamingWarn(
				`data channel still not open after ${timeoutMs}ms, sending anyway`,
				describeStreamingSession(pixelStreaming),
			);
			send();
		}
	}, intervalMs);
	return () => window.clearInterval(timer);
};

/** The Unreal app reports `{ scene: { loadingstatus: "done" } }` once a scene is in. */
export const isSceneLoadedResponse = (response: string): boolean => {
	if (!response) return false;
	try {
		const json = JSON.parse(response);
		return json?.scene?.loadingstatus === "done";
	} catch (e) {
		return false;
	}
};

/**
 * What the cloud is doing while the user waits: either a status line from the
 * Arcware platform ("love letter", e.g. that an instance is booting) or a
 * queue notice when no rendering server is free.
 */
export type StreamingStatus =
	| { kind: "message"; text: string }
	| { kind: "queue"; estimatedWaitTime?: number; valueType?: string }
	| null;

export const loveLetterToStatus = (letter: any): StreamingStatus => {
	const text = String(letter?.reason ?? "")
		.replace(/^LL:\s*/, "")
		.trim();
	return text ? { kind: "message", text } : null;
};

export const queueToStatus = (message: any): StreamingStatus => {
	const queue = message?.queue;
	if (!queue) return null;
	return {
		kind: "queue",
		estimatedWaitTime: queue.estimatedWaitTime,
		valueType: queue.valueType,
	};
};

/* ------------------------------------------------------------------------ */
/* Diagnostics                                                              */
/* ------------------------------------------------------------------------ */

const DEBUG_STORAGE_KEY = "hegias.streamingDebug";

/**
 * Verbose Navigator logging: on in every non-production build and on stage,
 * and switchable on in production from the browser console with
 * `localStorage.setItem("hegias.streamingDebug", "1")`. Errors and warnings
 * from the streaming side are logged regardless of this flag.
 */
export const isStreamingDebug = (): boolean => {
	try {
		if (window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1") return true;
	} catch (e) {
		// storage blocked: fall through
	}
	return (
		process.env.NODE_ENV !== "production" ||
		window.location.hostname.includes("hegias-stage.com")
	);
};

const stamp = () => new Date().toISOString().slice(11, 23);

export const streamingLog = (...args: unknown[]) => {
	if (isStreamingDebug()) console.log(`[streaming ${stamp()}]`, ...args);
};
export const streamingWarn = (...args: unknown[]) => {
	console.warn(`[streaming ${stamp()}]`, ...args);
};
export const streamingError = (...args: unknown[]) => {
	console.error(`[streaming ${stamp()}]`, ...args);
};

const WS_STATE_LABELS = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"];

/** One-line picture of where a session stands, for the console. */
export const describeStreamingSession = (pixelStreaming: any) => {
	const controller = pixelStreaming?.webRtcController;
	const ws = getWebSocketReadyState(pixelStreaming);
	const pc = controller?.peerConnectionController?.peerConnection;
	const dc = controller?.sendrecvDataChannelController?.dataChannel;
	let video: HTMLVideoElement | undefined;
	try {
		video = controller?.videoPlayer?.getVideoElement?.();
	} catch (e) {
		video = undefined;
	}
	return {
		webSocket: ws === undefined ? "none" : WS_STATE_LABELS[ws] ?? ws,
		session: pixelStreaming?.session?.current ?? null,
		streamer: controller?.subscribedStream ?? null,
		peerConnection: pc?.connectionState ?? "none",
		ice: pc?.iceConnectionState ?? "none",
		dataChannel: dc?.readyState ?? "none",
		video: video
			? `${video.videoWidth}x${video.videoHeight} readyState=${video.readyState} paused=${video.paused} t=${video.currentTime.toFixed(1)}s`
			: "none",
		videoInitializedSent: pixelStreaming?.videoInitializedSent ?? null,
		hasStreamInfo: !!pixelStreaming?.streamInfo,
	};
};

// Events the Epic frontend dispatches through PixelStreaming.addEventListener
const SDK_EVENTS_ERROR = [
	"webRtcFailed",
	"playStreamError",
	"dataChannelError",
	"subscribeFailed",
];
const SDK_EVENTS_WARN = [
	"webRtcDisconnected",
	"playStreamRejected",
	"dataChannelClose",
	"afkWarningActivate",
	"afkTimedOut",
];
const SDK_EVENTS_INFO = [
	"streamConnect",
	"streamReconnect",
	"streamDisconnect",
	"webRtcAutoConnect",
	"webRtcConnecting",
	"webRtcConnected",
	"webRtcSdpOffer",
	"webRtcSdpAnswer",
	"webRtcTCPRelayDetected",
	"dataChannelOpen",
	"videoInitialized",
	"streamLoading",
	"playStream",
	"streamerListMessage",
	"initialSettings",
	"playerCount",
	"afkWarningDeactivate",
];

// Signalling messages that carry nothing worth reading, or too much of it
// iceCandidate: a dozen or two per connection, nothing to read in them
const SIGNALLING_SKIP = new Set([
	"ping",
	"pong",
	"stats",
	"keepalive",
	"iceCandidate",
]);
const SIGNALLING_TYPE_ONLY = new Set([
	"config",
	"offer",
	"answer",
	"iceCandidate",
	"version",
	"render",
]);

/**
 * Logs everything the streaming side tells us: Epic frontend events, Arcware
 * platform messages (errors, "love letters", queue, session id), every
 * signalling message in and out, and every WebSocket close. Errors and
 * warnings always reach the console; the rest only when isStreamingDebug().
 *
 * Returns a function that removes all of it again.
 */
export const attachStreamingDiagnostics = (pixelStreaming: any): (() => void) => {
	const cleanups: Array<() => void> = [];
	if (!pixelStreaming) return () => {};

	const onSdkEvent = (level: "error" | "warn" | "info") => (event: any) => {
		const payload = event?.data !== undefined ? event.data : "";
		if (level === "error") streamingError(`SDK event ${event?.type}`, payload);
		else if (level === "warn") streamingWarn(`SDK event ${event?.type}`, payload);
		else streamingLog(`SDK event ${event?.type}`, payload);
	};
	const listen = (types: string[], level: "error" | "warn" | "info") => {
		const handler = onSdkEvent(level);
		types.forEach((type) => {
			try {
				pixelStreaming.addEventListener?.(type, handler);
				cleanups.push(() => pixelStreaming.removeEventListener?.(type, handler));
			} catch (e) {
				// an event type this SDK build does not know: ignore
			}
		});
	};
	listen(SDK_EVENTS_ERROR, "error");
	listen(SDK_EVENTS_WARN, "warn");
	listen(SDK_EVENTS_INFO, "info");

	// Arcware platform handlers (EventHandler.add / .remove)
	const addArcware = (name: string, fn: (arg: any) => void) => {
		const handler = pixelStreaming[name];
		if (handler?.add) {
			handler.add(fn);
			cleanups.push(() => handler.remove?.(fn));
		}
	};
	addArcware("errorHandler", (error) => streamingError("Arcware error message", error));
	addArcware("loveLetterHandler", (letter) => streamingLog("Arcware status", letter?.reason));
	addArcware("queueHandler", (queue) => streamingWarn("Arcware queue", queue?.queue));
	addArcware("sessionIdHandler", (id) => streamingLog("Arcware session id", id));
	addArcware("websocketOnCloseHandler", (event: CloseEvent) =>
		streamingWarn(
			`WebSocket closed code=${event?.code} reason="${event?.reason}" wasClean=${event?.wasClean}`,
		),
	);

	// Raw signalling traffic
	const transport = pixelStreaming.webRtcController?.transport;
	const onIn = (message: any) => {
		const type = message?.type;
		if (SIGNALLING_SKIP.has(type)) return;
		if (type === "error") {
			streamingError("signalling error <=", message);
		} else if (SIGNALLING_TYPE_ONLY.has(type)) {
			streamingLog(`signalling <= ${type}`);
		} else {
			streamingLog(`signalling <= ${type}`, message);
		}
	};
	const onOut = (message: any) => {
		const type = message?.type;
		if (SIGNALLING_SKIP.has(type)) return;
		streamingLog(`signalling => ${type}`);
	};
	const subscribe = (event: string, fn: (m: any) => void) => {
		const add = transport?.addListener ?? transport?.on;
		const remove = transport?.removeListener ?? transport?.off;
		if (typeof add !== "function") return;
		add.call(transport, event, fn);
		cleanups.push(() => {
			if (typeof remove === "function") remove.call(transport, event, fn);
		});
	};
	subscribe("message", onIn);
	subscribe("out", onOut);

	streamingLog("diagnostics attached", describeStreamingSession(pixelStreaming));
	return () => {
		cleanups.splice(0).forEach((fn) => {
			try {
				fn();
			} catch (e) {
				// already gone
			}
		});
	};
};

/**
 * Prints a session snapshot every `intervalMs` while the caller keeps it
 * running — meant for the stretch between "start" and "scene loaded", so a
 * Navigator that hangs says where in the console. Returns the stop function.
 */
export const startStreamingWatchdog = (
	getSnapshot: () => Record<string, unknown>,
	intervalMs = 10000,
): (() => void) => {
	const startedAt = Date.now();
	const timer = window.setInterval(() => {
		const waited = Math.round((Date.now() - startedAt) / 1000);
		streamingWarn(`still waiting after ${waited}s`, getSnapshot());
	}, intervalMs);
	return () => window.clearInterval(timer);
};
